/*
  li-design-strip.js
  Lucky Inger Design: swipe gallery on product cards + client portal search.

  Gallery (snippets/li-design-strip.liquid):
  - Arrows (desktop hover) and finger swipes (phones) move through a product's designs.
  - The sliding images are built from template[data-li-all] on first hover / touch.
  - Card links follow the design on show (?variant=ID&design=NAME), and
    li-order-store.js opens the order page on that design.
  - Meta line under the price shows the design name and the design count.
  - Colour swatches under that, one dot per colour the design on show comes in.
    Hovering (or tapping) a dot swaps the card image to that colour's real
    photo and names the colour, so a client can check the colours from the
    collection page without opening the product. Read from data-li-cols, a flat
    "design::colour::variantId::imageUrl||..." string the snippet emits;
    grouping happens here rather than in Liquid.
  - Hover / touch prefetches the product page so it opens faster.

  Portal search (sections/li-collection-portal.liquid, [data-li-portal]):
  - Typing turns every matching design into its own tile (no swiping) in the same
    type order as the grid. Matches design names, product names, tags and type.
  - Clear (x) or Escape brings the gallery grid back.

  One document-level listener set, safe to load more than once.
*/
(function () {
  'use strict';
  if (window.__liGallery) return;
  window.__liGallery = true;

  var cache = new WeakMap();
  var colCache = new WeakMap();

  function closestSafe(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function cardOf(el) {
    return closestSafe(el, '.product-card-wrapper') || closestSafe(el, '.card-wrapper');
  }
  function galOf(el) {
    var card = cardOf(el);
    return card ? card.querySelector('[data-li-gal]') : null;
  }

  function designs(gal) {
    var list = cache.get(gal);
    if (list) return list;
    list = [];
    var tpl = gal.querySelector('template[data-li-all]');
    if (tpl && tpl.content) {
      tpl.content.querySelectorAll('[data-li-d]').forEach(function (s) {
        var d = s.dataset;
        list.push({
          id: d.variantId || '',
          design: d.design || '',
          param: d.designParam || '',
          src: d.src || '',
          srcset: d.srcset || ''
        });
      });
    }
    cache.set(gal, list);
    return list;
  }

  /* design name -> [{ name, id, src }], deduped, in first-seen order */
  function coloursOf(gal) {
    var map = colCache.get(gal);
    if (map) return map;
    map = {};
    var raw = gal.getAttribute('data-li-cols') || '';
    raw.split('||').forEach(function (rec) {
      if (!rec) return;
      var bits = rec.split('::');
      if (bits.length < 2) return;
      var design = bits[0].trim();
      var colour = bits[1].trim();
      if (!colour) return;
      var list = map[design] || (map[design] = []);
      var seen = list.some(function (c) { return c.name === colour; });
      if (seen) return;
      list.push({
        name: colour,
        id: (bits[2] || '').trim(),
        // An image URL contains ":" but never "::", so anything past the third
        // separator is the tail of the URL and goes back together.
        src: bits.length > 3 ? bits.slice(3).join('::').trim() : ''
      });
    });
    colCache.set(gal, map);
    return map;
  }

  /* "4 designs", or "" on a single unnamed design. Colours are the swatch row's
     job; only when there is no swatch row does the count carry them. */
  function countText(list, cols, swatched) {
    var parts = [];
    var hasNames = list.some(function (d) { return d.design; });
    if (hasNames) parts.push(list.length + (list.length === 1 ? ' design' : ' designs'));
    if (!swatched && cols.length > 1) parts.push(cols.length + ' colours');
    return parts.join(' · ');
  }

  /* ---------- Colour swatches ---------- */
  /* Rough hex per colour name, only so the dot reads at a glance. The real
     answer is the photo it swaps in, so an unlisted colour is still checkable. */
  var HEX = {
    black: '#1a1a1a', white: '#f4f3f0', ecru: '#e4dac6', bone: '#e8e2d6',
    cream: '#ece2cf', natural: '#e3d9c6', sand: '#d6c6a8', stone: '#cfc7ba',
    walnut: '#6d5443', brown: '#5e4230', tan: '#b08a5e', camel: '#c19a6b',
    chocolate: '#43302a', coffee: '#4a3728', beige: '#d9cbb3',
    cypress: '#4a5443', army: '#4b4f3a', olive: '#6b6b34', khaki: '#a99a72',
    sage: '#9aa68f', 'pea green': '#7d8c46', pea: '#7d8c46',
    'kelly green': '#2e8b50', green: '#2e7d32', forest: '#1f4d2e',
    'bottle green': '#14402a', emerald: '#1f7a5a', mint: '#a8ddc4', lime: '#93c83e',
    navy: '#1f2a44', 'french navy': '#1f2a44', midnight: '#101a2e',
    cobalt: '#2b4f9e', sapphire: '#1e3a6e', royal: '#1f4fa8', blue: '#2b5cc4',
    sky: '#7fb9e0', 'light blue': '#a9cfe8', denim: '#4a6b8a', ice: '#cfe6ee',
    aqua: '#63c7c0', teal: '#1f6f6b', turquoise: '#2fb3a8',
    fog: '#c6cbce', 'grey marle': '#a7a49f', 'gray marle': '#a7a49f',
    marle: '#a7a49f', grey: '#8d8d8d', gray: '#8d8d8d', silver: '#c4c4c4',
    charcoal: '#3f4144', slate: '#5b6468', ash: '#cfcfcf', graphite: '#35383b',
    'hot pink': '#e5308a', pink: '#f0a8bf', 'dusty pink': '#d9a2a8',
    blush: '#eec9c6', fuchsia: '#c62b7a', fusia: '#c62b7a',
    red: '#c1272d', maroon: '#6b1f2a', burgundy: '#6b1f2a', wine: '#5a1b28',
    rust: '#a8502a', orange: '#e2701e', coral: '#e0715f', peach: '#f0b49a',
    purple: '#5b3a8e', plum: '#5a2f4a', lilac: '#b9a6d6', lavender: '#cbbfe4',
    yellow: '#f2c53d', gold: '#d4a02a', mustard: '#c8962a', butter: '#f2e2a0'
  };
  var HEX_TOKENS = Object.keys(HEX).sort(function (a, b) { return b.length - a.length; });

  function hexFor(name) {
    var k = String(name || '').toLowerCase().trim().replace(/\s+/g, ' ');
    if (HEX[k]) return HEX[k];
    for (var i = 0; i < HEX_TOKENS.length; i++) {
      if (k.indexOf(HEX_TOKENS[i]) !== -1) return HEX[HEX_TOKENS[i]];
    }
    return null;
  }

  /* "Black/White" style names get a split dot rather than a guess */
  function dotBackground(name) {
    var halves = String(name || '').split('/');
    if (halves.length === 2) {
      var a = hexFor(halves[0]);
      var b = hexFor(halves[1]);
      if (a && b) return 'linear-gradient(135deg, ' + a + ' 0 50%, ' + b + ' 50% 100%)';
    }
    var one = hexFor(name);
    return one || 'repeating-linear-gradient(135deg, #cfcfcf 0 4px, #e6e6e6 4px 8px)';
  }

  var MAX_DOTS = 8;

  /* Returns true when a swatch row is on the card for this design. */
  function paintSwatches(gal, meta, cols) {
    var row = meta.nextElementSibling;
    if (!row || !row.hasAttribute || !row.hasAttribute('data-li-sw')) row = null;
    if (cols.length < 2) {
      if (row) row.parentNode.removeChild(row);
      return false;
    }
    if (!row) {
      row = document.createElement('div');
      row.className = 'li-gal-sw';
      row.setAttribute('data-li-sw', '');
      row.setAttribute('role', 'img');
      if (meta.parentNode) meta.parentNode.insertBefore(row, meta.nextSibling);
    }
    // Same colours as last paint: leave the nodes alone. Replacing them under a
    // pointer that is already on one of them loses the hover.
    var sig = cols.map(function (c) { return c.name + '~' + c.id; }).join('|');
    if (row.getAttribute('data-li-sig') === sig) return true;
    row.setAttribute('data-li-sig', sig);
    row.setAttribute('aria-label', 'Colours: ' + cols.map(function (c) { return c.name; }).join(', '));
    var show = cols.length > MAX_DOTS ? cols.slice(0, MAX_DOTS - 1) : cols;
    var html = show.map(function (c) {
      return (
        '<span class="li-gal-sw__dot" data-li-sw-dot data-li-c="' + esc(c.name) + '"' +
        ' data-li-v="' + esc(c.id) + '" data-li-src="' + esc(c.src) + '"' +
        ' style="background: ' + dotBackground(c.name) + ';"></span>'
      );
    }).join('');
    if (cols.length > MAX_DOTS) {
      html += '<span class="li-gal-sw__more">+' + (cols.length - show.length) + '</span>';
    }
    row.innerHTML = html;
    return true;
  }

  /* Name, count and swatches for the design on show */
  function paintMeta(gal, list, i) {
    var card = cardOf(gal);
    var meta = card ? card.querySelector('[data-li-meta]') : null;
    if (!meta) return;
    var d = list[i] || { design: '' };
    var cols = coloursOf(gal)[d.design] || [];
    var nameEl = meta.querySelector('[data-li-name]');
    if (nameEl) nameEl.textContent = d.design;
    var swatched = paintSwatches(gal, meta, cols);
    var countEl = meta.querySelector('[data-li-count]');
    if (countEl) countEl.textContent = countText(list, cols, swatched);
  }

  /* The image the client is actually looking at: the slide when the gallery has
     taken over, otherwise the theme's own card image. */
  function visibleImg(gal) {
    var track = gal.querySelector('.li-gal-track.is-live');
    if (track) {
      var imgs = track.querySelectorAll('.li-gal-rail img');
      return imgs[+gal.dataset.liI || 0] || null;
    }
    var card = cardOf(gal);
    return card ? card.querySelector('.card__media img') : null;
  }

  var active = null;

  function swapImage(gal, src) {
    if (!src) return;
    var img = visibleImg(gal);
    if (!img) return;
    if (!img.dataset.liOrigSrc) {
      img.dataset.liOrigSrc = img.getAttribute('src') || img.dataset.src || '';
      img.dataset.liOrigSrcset = img.getAttribute('srcset') || img.dataset.srcset || '';
    }
    gal.__liSwapped = img;
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
  }

  function restoreImage(gal) {
    var img = gal.__liSwapped;
    if (!img) return;
    gal.__liSwapped = null;
    var s = img.dataset.liOrigSrc;
    var ss = img.dataset.liOrigSrcset;
    delete img.dataset.liOrigSrc;
    delete img.dataset.liOrigSrcset;
    if (ss) img.setAttribute('srcset', ss);
    else img.removeAttribute('srcset');
    if (s) img.setAttribute('src', s);
  }

  function showColour(gal, dot) {
    if (active && active.dot === dot) return;
    clearColour();
    active = { gal: gal, dot: dot };
    dot.classList.add('is-on');
    swapImage(gal, dot.dataset.liSrc);
    var card = cardOf(gal);
    var countEl = card ? card.querySelector('[data-li-count]') : null;
    if (countEl) countEl.textContent = dot.dataset.liC;
    if (dot.dataset.liV && card) {
      var list = designs(gal);
      var d = list[+gal.dataset.liI || 0];
      var url = withDesign(gal.dataset.liUrl || '', dot.dataset.liV, d ? d.param : '');
      card.querySelectorAll('[data-li-card-link]').forEach(function (a) {
        a.setAttribute('href', url);
      });
      warm(url);
    }
  }

  function clearColour() {
    if (!active) return;
    var a = active;
    active = null;
    a.dot.classList.remove('is-on');
    restoreImage(a.gal);
    var list = designs(a.gal);
    var i = +a.gal.dataset.liI || 0;
    var card = cardOf(a.gal);
    // Put the count line back without rebuilding the row: the pointer may
    // already be on the next dot, and replacing it mid-move loses the events.
    var countEl = card ? card.querySelector('[data-li-count]') : null;
    if (countEl) {
      var cols = coloursOf(a.gal)[(list[i] || {}).design] || [];
      countEl.textContent = countText(list, cols, cols.length > 1);
    }
    if (card) {
      var url = designUrl(a.gal, list[i]);
      card.querySelectorAll('[data-li-card-link]').forEach(function (b) {
        b.setAttribute('href', url);
      });
    }
  }

  /* ---------- Links + prefetch ---------- */
  function withDesign(url, id, design) {
    if (!url) return url;
    var base = url.split('#')[0];
    var q = base.indexOf('?');
    var path = q === -1 ? base : base.slice(0, q);
    var parts = q === -1 ? [] : base.slice(q + 1).split('&').filter(function (p) {
      return p && p.indexOf('variant=') !== 0 && p.indexOf('design=') !== 0;
    });
    if (id) parts.push('variant=' + id);
    if (design) parts.push('design=' + design);
    return parts.length ? path + '?' + parts.join('&') : path;
  }
  function designUrl(gal, d) {
    if (!d || !d.param) return gal.dataset.liUrl || '';
    return withDesign(gal.dataset.liUrl || '', d.id, d.param);
  }

  var warmed = {};
  function warm(url) {
    if (!url || warmed[url]) return;
    warmed[url] = true;
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = url;
      l.as = 'document';
      document.head.appendChild(l);
    } catch (e) {}
  }

  /* ---------- Meta ---------- */
  function initCard(gal) {
    if (gal.dataset.liReady) return;
    gal.dataset.liReady = '1';
    paintMeta(gal, designs(gal), +gal.dataset.liI || 0);
  }
  function initAll(root) {
    (root || document).querySelectorAll('[data-li-gal]').forEach(initCard);
  }

  /* ---------- Gallery ---------- */
  function load(img) {
    if (!img || img.dataset.liLoaded) return;
    img.dataset.liLoaded = '1';
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
  }

  function build(gal) {
    var track = gal.querySelector('.li-gal-track');
    if (track) return track;
    var list = designs(gal);
    if (list.length < 2) return null;
    var card = cardOf(gal);
    var orig = card ? card.querySelector('.card__media img') : null;
    var sizes = orig ? orig.getAttribute('sizes') : '';

    track = document.createElement('div');
    track.className = 'li-gal-track';
    track.setAttribute('aria-hidden', 'true');
    var rail = document.createElement('div');
    rail.className = 'li-gal-rail';
    list.forEach(function (d) {
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      if (sizes) img.sizes = sizes;
      img.dataset.src = d.src;
      img.dataset.srcset = d.srcset;
      rail.appendChild(img);
    });
    track.appendChild(rail);
    gal.insertBefore(track, gal.firstChild);
    setRail(gal, +gal.dataset.liI || 0, 0);
    loadAround(gal, +gal.dataset.liI || 0);
    return track;
  }

  function loadAround(gal, i) {
    var imgs = gal.querySelectorAll('.li-gal-rail img');
    [i - 1, i, i + 1].forEach(function (k) {
      if (k >= 0 && k < imgs.length) load(imgs[k]);
    });
  }

  function setRail(gal, i, px) {
    var rail = gal.querySelector('.li-gal-rail');
    if (!rail) return;
    rail.style.transform = 'translateX(calc(' + (-100 * i) + '% + ' + (px || 0) + 'px))';
  }

  function go(gal, to) {
    var list = designs(gal);
    var n = list.length;
    if (n < 2) return;
    // A colour preview belongs to the design it was opened on
    if (active && active.gal === gal) clearColour();
    to = ((to % n) + n) % n;
    var track = build(gal);
    loadAround(gal, to);
    gal.dataset.liI = to;
    if (track) track.classList.add('is-live');
    setRail(gal, to, 0);

    gal.querySelectorAll('.li-gal-segs i').forEach(function (s, k) {
      s.classList.toggle('is-on', k === to);
    });
    var bar = gal.querySelector('.li-gal-prog i');
    if (bar) bar.style.width = ((to + 1) / n) * 100 + '%';

    var card = cardOf(gal);
    if (!card) return;
    /* Name, count and swatches all belong to the design on show. */
    paintMeta(gal, list, to);
    var url = designUrl(gal, list[to]);
    card.querySelectorAll('[data-li-card-link]').forEach(function (a) {
      a.setAttribute('href', url);
    });
    warm(url);
  }

  // Warm up on first hover / touch: build the slides and prefetch the page
  function engage(e) {
    var gal = galOf(e.target);
    if (!gal) return;
    initCard(gal);
    build(gal);
    var a = cardOf(gal).querySelector('[data-li-card-link]');
    if (a) warm(a.getAttribute('href'));
  }
  document.addEventListener('pointerenter', engage, true);
  document.addEventListener('focusin', engage);

  /* Swatches. pointerover covers mouse hover and the first touch alike: a tap
     on a dot previews that colour and it stays up until something else is
     pointed at, which is what you want on a phone. */
  document.addEventListener('pointerover', function (e) {
    var dot = closestSafe(e.target, '[data-li-sw-dot]');
    if (!dot) {
      clearColour();
      return;
    }
    var gal = galOf(dot);
    if (gal) showColour(gal, dot);
  });
  document.addEventListener(
    'click',
    function (e) {
      var dot = closestSafe(e.target, '[data-li-sw-dot]');
      if (!dot) return;
      // Previewing a colour is not a trip to the product page
      e.preventDefault();
      e.stopPropagation();
      var gal = galOf(dot);
      if (gal) showColour(gal, dot);
    },
    true
  );

  // Arrows (capture so the card link never fires)
  document.addEventListener(
    'click',
    function (e) {
      var ar = closestSafe(e.target, '[data-li-dir]');
      if (ar) {
        e.preventDefault();
        e.stopPropagation();
        var gal = closestSafe(ar, '[data-li-gal]');
        go(gal, (+gal.dataset.liI || 0) + +ar.dataset.liDir);
        return;
      }
      // A swipe must not count as a tap on the card
      var g = galOf(e.target);
      if (g && g.dataset.liSwiped) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  // Finger swipe with follow
  var drag = null;
  document.addEventListener(
    'touchstart',
    function (e) {
      var inner = closestSafe(e.target, '.card__inner');
      if (!inner || closestSafe(e.target, '[data-li-dir]')) return;
      var gal = inner.querySelector('[data-li-gal]');
      if (!gal) return;
      engage(e);
      if (designs(gal).length < 2) return;
      var t = e.touches[0];
      drag = { gal: gal, x: t.clientX, y: t.clientY, dx: 0, lock: null, w: inner.clientWidth || 1 };
    },
    { passive: true }
  );
  document.addEventListener(
    'touchmove',
    function (e) {
      if (!drag) return;
      var t = e.touches[0];
      var dx = t.clientX - drag.x;
      var dy = t.clientY - drag.y;
      if (drag.lock === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        drag.lock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.lock === 'x') {
          drag.gal.classList.add('li-gal--drag');
          var track = build(drag.gal);
          if (track) track.classList.add('is-live');
        }
      }
      if (drag.lock !== 'x') return;
      drag.dx = dx;
      var i = +drag.gal.dataset.liI || 0;
      var n = designs(drag.gal).length;
      // Resist past the ends
      if ((i === 0 && dx > 0) || (i === n - 1 && dx < 0)) dx = dx / 3;
      setRail(drag.gal, i, dx);
      loadAround(drag.gal, i + (dx < 0 ? 1 : -1));
    },
    { passive: true }
  );
  function endDrag() {
    if (!drag) return;
    var d = drag;
    drag = null;
    d.gal.classList.remove('li-gal--drag');
    if (d.lock !== 'x') return;
    d.gal.dataset.liSwiped = '1';
    setTimeout(function () {
      delete d.gal.dataset.liSwiped;
    }, 400);
    var i = +d.gal.dataset.liI || 0;
    var n = designs(d.gal).length;
    var to = i;
    if (Math.abs(d.dx) > d.w * 0.16) to = i + (d.dx < 0 ? 1 : -1);
    to = Math.max(0, Math.min(n - 1, to));
    if (to === i) {
      setRail(d.gal, i, 0);
    } else {
      go(d.gal, to);
    }
  }
  document.addEventListener('touchend', endDrag);
  document.addEventListener('touchcancel', endDrag);

  /* ---------- Portal search ---------- */
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function hl(text, q) {
    if (!q) return esc(text);
    var i = text.toLowerCase().indexOf(q);
    if (i === -1) return esc(text);
    return esc(text.slice(0, i)) + '<mark>' + esc(text.slice(i, i + q.length)) + '</mark>' + esc(text.slice(i + q.length));
  }

  function setupPortal(portal) {
    if (portal.dataset.liSearchReady) return;
    var input = portal.querySelector('[data-li-search]');
    var grid = portal.querySelector('[data-li-grid]');
    var results = portal.querySelector('[data-li-results]');
    if (!input || !grid || !results) return;
    portal.dataset.liSearchReady = '1';

    var wrap = portal.querySelector('[data-li-search-wrap]');
    var status = portal.querySelector('[data-li-status]');
    var clear = portal.querySelector('[data-li-clear]');
    var total = portal.querySelector('[data-li-total]');

    // Index every card once (DOM order = type order)
    var index = [];
    var designTotal = 0;
    grid.querySelectorAll('.grid__item').forEach(function (item) {
      var gal = item.querySelector('[data-li-gal]');
      if (!gal) return;
      var list = designs(gal);
      var named = list.some(function (d) { return d.design; });
      designTotal += named ? list.length : 1;
      var badges = '';
      item.querySelectorAll('.product-badge').forEach(function (b) { badges += b.outerHTML; });
      var sizes = (item.querySelector('.card__media img') || {}).sizes || '';
      index.push({
        gal: gal,
        list: list,
        colours: coloursOf(gal),
        title: gal.dataset.liTitle || '',
        price: gal.dataset.liPrice || '',
        keys: (gal.dataset.liKeys || '').toLowerCase(),
        badges: badges,
        sizes: sizes,
        barmat: !!closestSafe(gal, '.li-barmat')
      });
    });
    if (total) total.textContent = designTotal;

    function tile(p, d, q) {
      var title = d.design || p.title;
      var cols = (p.colours[d.design] || []);
      var colNote = cols.length > 1 ? ' · ' + cols.length + ' colours' : '';
      var sub = d.design ? hl(p.title, q) + ' · ' + esc(p.price) + colNote : esc(p.price) + colNote;
      return (
        '<li class="grid__item">' +
          '<a class="li-hit' + (p.barmat ? ' li-barmat-hit' : '') + '" href="' + esc(designUrl(p.gal, d)) + '">' +
            '<span class="li-hit__img">' +
              '<img src="' + esc(d.src) + '" srcset="' + esc(d.srcset) + '"' + (p.sizes ? ' sizes="' + esc(p.sizes) + '"' : '') + ' alt="" loading="lazy" decoding="async">' +
              '<span class="li-hit__go" aria-hidden="true">Order</span>' +
            '</span>' +
            p.badges +
            '<span class="li-hit__body">' +
              '<span class="li-hit__title">' + hl(title, q) + '</span>' +
              '<span class="li-hit__sub">' + sub + '</span>' +
            '</span>' +
          '</a>' +
        '</li>'
      );
    }

    function render() {
      var raw = input.value.trim();
      var q = raw.toLowerCase();
      if (wrap) wrap.classList.toggle('is-filled', !!raw);
      if (!q) {
        results.hidden = true;
        results.innerHTML = '';
        grid.hidden = false;
        if (status) status.innerHTML = '';
        portal.classList.remove('li-portal--searching');
        return;
      }
      var html = '';
      var count = 0;
      index.forEach(function (p) {
        var productHit = p.title.toLowerCase().indexOf(q) !== -1 || p.keys.indexOf(q) !== -1;
        p.list.forEach(function (d) {
          // A colour name is a fair thing to search for, now that colours no
          // longer have tiles of their own.
          var colourHit = (p.colours[d.design] || []).some(function (c) {
            return c.name.toLowerCase().indexOf(q) !== -1;
          });
          if (productHit || colourHit || d.design.toLowerCase().indexOf(q) !== -1) {
            html += tile(p, d, q);
            count++;
          }
        });
      });
      grid.hidden = true;
      results.hidden = false;
      portal.classList.add('li-portal--searching');
      if (count) {
        results.innerHTML = html;
        if (status) status.innerHTML = '<b>' + count + '</b> ' + (count === 1 ? 'design matches' : 'designs match');
      } else {
        results.innerHTML =
          '<li class="li-portal-noresults">No designs match <b>"' + esc(raw) + '"</b>.<br>Try a colour, a design name, or a product like "tee" or "cap".</li>';
        if (status) status.innerHTML = '<b>0</b> designs match';
      }
    }

    input.addEventListener('input', render);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.value) {
        input.value = '';
        render();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      }
    });
    if (clear) {
      clear.addEventListener('click', function (e) {
        e.preventDefault();
        input.value = '';
        render();
        input.focus();
      });
    }
    results.addEventListener('pointerover', function (e) {
      var a = closestSafe(e.target, '.li-hit');
      if (a) warm(a.getAttribute('href'));
    });
    // Browser restored a typed value (back button)
    if (input.value) render();
  }

  /* ---------- Start ---------- */
  function start() {
    initAll(document);
    document.querySelectorAll('[data-li-portal]').forEach(setupPortal);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Grids re-rendered by filters / AJAX
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      start();
    }, 60);
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Back/forward cache: keep links matching the design on show
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-li-gal]').forEach(function (gal) {
      if (gal.querySelector('.li-gal-track.is-live')) {
        var i = +gal.dataset.liI || 0;
        gal.dataset.liI = -1;
        go(gal, i);
      }
    });
  });
})();